import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Row, Layout } from 'antd';
import { DragDropContext } from 'react-beautiful-dnd';

import dndStructure from './initStructure/dndStructure.js';
import sampleTrainings from './initStructure/SampleTrainings.js';
import ListOfTrainings from './ListOfTrainings.js';
import SelectedTrainings from './SelectedTrainings.js';
import Statistics from './Statistics.js';
import TrainingCSPModalForm from './TrainingCSPModalForm.js';
import TrainingBrowseCSPModalForm from './TrainingBrowseCSPModalForm.js';
import {checkColumnStructure, checkTrainingsStructure, verifyTrainingValues} from './utils.js';

const { Content } = Layout;

const Trainings = ({ dbTrainings, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, rsProfiles, dbDomains, dbECSFRoles }) => {
  const [trainings, setTrainings] = useState({});
  const [columns, setColumns] = useState(dndStructure.columns);
  const [error, setError] = useState(null);
  const [draggedTraining, setDraggedTraining] = useState(null);
  const [sampleLoaded, setSampleLoaded] = useState(false);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [addModalData, setAddModalData] = useState(null);
  const [modalTrainingId, setModalTrainingId] = useState(null);

  const initialColumns = useMemo(() => JSON.parse(JSON.stringify(dndStructure.columns)), []);

  const location = useLocation();
  useEffect(() => {
    setColumns(initialColumns);
  }, [location.pathname, initialColumns]);

  const openAddModal = (id) => {
    setModalTrainingId(id);
    setAddModalData(id ? trainings[id] : 'new');
  };

  const closeAddModal = () => {
    setAddModalData(null);
    setModalTrainingId(null);
  };

  const openBrowseModal = () => setShowBrowseModal(true);
  const closeBrowseModal = () => setShowBrowseModal(false);

  const createDnDId = (name, prefix, pool) => {
    let base = `${prefix}_${name.replace(/ /g, '_').toLowerCase()}`;
    let i = 0;
    while (pool.includes(`${base}_${i}`)) i++;
    return `${base}_${i}`;
  };

  const submitBrowse = (selectedIds) => {
    let newCols = { ...columns };
    let newTrains = { ...trainings };

    selectedIds.forEach((id) => {
      const item = dbTrainings.find((i) => i.id === id);
      if (!item) return;
      const copy = JSON.parse(JSON.stringify(item));
      copy.id = createDnDId(copy.name, 'db', Object.keys(newTrains));
      newCols.available.trainingIds.push(copy.id);
      newTrains[copy.id] = copy;
    });

    setColumns(newCols);
    setTrainings(newTrains);
    closeBrowseModal();
  };

  const submitModal = (inputData) => {
    if (modalTrainingId) updateTrain(inputData);
    else addCustomTraining(inputData);
    closeAddModal();
  };

  const addCustomTraining = (input) => {
    const copy = { ...input };
    copy.id = createDnDId(copy.name, 'user', Object.keys(trainings));
    setTrainings((prev) => ({ ...prev, [copy.id]: copy }));
    setColumns((prev) => ({
      ...prev,
      available: {
        ...prev.available,
        trainingIds: [...prev.available.trainingIds, copy.id],
      },
    }));
  };
  const updateTrain = (input) => {
    if (!modalTrainingId) return;
    setTrainings((prev) => ({
      ...prev,
      [modalTrainingId]: { ...prev[modalTrainingId], ...input, id: modalTrainingId },
    }));
  };

  const removeForeverTraining = ({ id, position }) => {
    setColumns((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        trainingIds: prev[position].trainingIds.filter((x) => x !== id),
      },
    }));
    setTrainings((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const removeTraining = ({ id, position }) => {
    setColumns((prev) => {
      const from = [...prev[position].trainingIds];
      const to = [...prev.available.trainingIds, id];
      return {
        ...prev,
        [position]: { ...prev[position], trainingIds: from.filter((x) => x !== id) },
        available: { ...prev.available, trainingIds: to },
      };
    });
  };

  const importData = (input) => {
  try {
    const parsed = JSON.parse(input);
    if (!parsed.trainings) throw new Error();
    if (
      !checkColumnStructure(parsed.trainings, dndStructure.columns) ||
      !checkTrainingsStructure(parsed.trainings)
    ) {
      setError({ message: 'Wrong input file format!' });
      return;
    }

    parsed.trainings.forEach((item) => {
      const itemWithValidValues = verifyTrainingValues(item, rsSkillsGroup.length);
      const copy = { ...itemWithValidValues };
      const id = createDnDId(copy.name, 'import', Object.keys(trainings));
      copy.id = id;
      const position = copy.position;
      delete copy.position;

      setTrainings((prev) => ({
        ...prev,
        [id]: copy,
      }));
      setColumns((prev) => {
        const col = prev[position];
        return {
          ...prev,
          [position]: {
            ...col,
            trainingIds: [...col.trainingIds, id],
          },
        };
      });
    });
  } catch {
    setError({ message: 'Sorry, something went wrong!' });
  }
};

  const loadSamples = () => {
    if (!sampleTrainings?.length) {
      setError({ message: 'There are no sample trainings available!' });
      return;
    }
    const sorted = [...sampleTrainings].sort((a, b) => a.name.localeCompare(b.name));
    let newCols = { ...columns };
    let newTrains = { ...trainings };
    sorted.forEach((item) => {
      const copy = { ...item };
      copy.id = createDnDId(copy.name, 'sample', Object.keys(newTrains));
      newCols.available.trainingIds.push(copy.id);
      newTrains[copy.id] = copy;
    });
    setColumns(newCols);
    setTrainings(newTrains);
    setSampleLoaded(true);
  };

  const onDragStart = (start) => {
    setDraggedTraining(start.draggableId);
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return setDraggedTraining(null);
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return setDraggedTraining(null);
    }
    const startCol = columns[source.droppableId];
    const endCol = columns[destination.droppableId];
    if (startCol === endCol) {
      const ids = Array.from(startCol.trainingIds);
      ids.splice(source.index, 1);
      ids.splice(destination.index, 0, draggableId);
      setColumns((prev) => ({ ...prev, [startCol.id]: { ...startCol, trainingIds: ids } }));
    } else {
      const startIds = Array.from(startCol.trainingIds);
      startIds.splice(source.index, 1);
      const finishIds = Array.from(endCol.trainingIds);
      finishIds.splice(destination.index, 0, draggableId);
      setColumns((prev) => ({
        ...prev,
        [startCol.id]: { ...startCol, trainingIds: startIds },
        [endCol.id]: { ...endCol, trainingIds: finishIds },
      }));
    }
    setDraggedTraining(null);
  };

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Alert message={error.message} type="error" showIcon />
        <Button type="link" onClick={() => setError(null)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <Layout className="csprofiler-trainings">
      <Content className="structure-content">
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Row gutter={16}>
            <ListOfTrainings
              columns={columns}
              trainings={trainings}
              draggedTraining={draggedTraining}
              sampleTrainingsLoaded={sampleLoaded}
              addCustomTraining={addCustomTraining}
              removeTraining={removeForeverTraining}
              loadSamples={loadSamples}
              openAddModal={openAddModal}
              openBrowseModal={openBrowseModal}
            />
            <SelectedTrainings
              columns={columns}
              trainings={trainings}
              draggedTraining={draggedTraining}
              removeTraining={removeTraining}
              importData={importData}
              openAddModal={openAddModal}
            />
            <Statistics rsSkillsGroup={rsSkillsGroup} rsProfiles={rsProfiles} trainings={columns.selected.trainingIds.map((id) => trainings[id])} />
          </Row>
        </DragDropContext>
      </Content>

      <TrainingCSPModalForm
        editRecord={addModalData}
        onCancel={closeAddModal}
        refresh={closeAddModal}
        saveLocally={submitModal}
        rsSkillsGroup={rsSkillsGroup}
        dbTrainings={dbTrainings}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />

      <TrainingBrowseCSPModalForm
        open={showBrowseModal}
        onCancel={closeBrowseModal}
        onLoad={submitBrowse}
        dbTrainings={dbTrainings}
        rsSkillsGroup={rsSkillsGroup}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />


    </Layout>
  );
};

export default Trainings;
